// ============================================================================
// CLEO'S SECRET — Check-in sliders
// File: src/components/program/CheckinSliders.jsx
//
// Four horizontal Likert sliders (1-10) for the weekly check-in form. Matches
// the dimension names + endpoint labels patients see on the Cleo Score page:
//
//   Hormonal balance · Metabolic health · Sleep and recovery · Vitality and mood
//   1 = "Struggling" · 10 = "Thriving"
//
// Native <input type="range"> with accentColor=P.GO for the thumb/track.
// Each slider shows: dimension name + current value (e.g. "7 / 10") +
// endpoint labels below the rail.
//
// Props:
//   values:    { hormonalScore, metabolicScore, sleepScore, vitalityScore }
//   onChange:  ({ ...same shape... }) => void — fires on every slider change
//   disabled:  true greys out + blocks interaction (used during submit)
// ============================================================================

import React from 'react';
import { P, FF } from '../../lib/design-tokens';

var DIMENSIONS = [
  { key: 'hormonalScore',  label: 'Hormonal balance' },
  { key: 'metabolicScore', label: 'Metabolic health' },
  { key: 'sleepScore',     label: 'Sleep and recovery' },
  { key: 'vitalityScore',  label: 'Vitality and mood' },
];

function Slider(props) {
  var label = props.label;
  var value = props.value;
  var disabled = props.disabled;
  var onChange = props.onChange;

  return (
    <div style={{
      marginBottom: 'clamp(22px, 2.4vw, 32px)',
      opacity: disabled ? 0.55 : 1,
    }}>
      {/* Top row: label + value */}
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        marginBottom: 10,
        gap: 12,
      }}>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(17px, 1.5vw, 19px)',
          fontWeight: 500,
          color: P.INK,
          letterSpacing: '-0.005em',
        }}>{label}</div>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(15px, 1.3vw, 17px)',
          fontWeight: 700,
          color: P.BR2,
          fontVariantNumeric: 'tabular-nums',
        }}>{value} / 10</div>
      </div>

      {/* Slider — native range with accentColor for cross-browser gold thumb */}
      <input
        type="range"
        min={1}
        max={10}
        step={1}
        value={value}
        disabled={disabled}
        onChange={function(e) {
          if (disabled) return;
          var n = parseInt(e.target.value, 10);
          if (!isNaN(n) && n >= 1 && n <= 10) onChange(n);
        }}
        style={{
          width: '100%',
          height: 28,
          accentColor: P.GO,
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: 'transparent',
        }}
      />

      {/* Endpoint labels */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 6,
        fontFamily: FF,
        fontSize: 'clamp(11px, 0.95vw, 13px)',
        fontWeight: 700,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        color: P.TF,
      }}>
        <span>Struggling</span>
        <span>Thriving</span>
      </div>
    </div>
  );
}

export default function CheckinSliders(props) {
  var values = props.values || {};
  var onChange = props.onChange || function() {};
  var disabled = !!props.disabled;

  function handleSliderChange(key, newValue) {
    var next = Object.assign({}, values);
    next[key] = newValue;
    onChange(next);
  }

  return (
    <div>
      {DIMENSIONS.map(function(d) {
        var v = values[d.key];
        if (typeof v !== 'number' || isNaN(v) || v < 1 || v > 10) v = 5;
        return (
          <Slider
            key={d.key}
            label={d.label}
            value={v}
            disabled={disabled}
            onChange={function(newValue) { handleSliderChange(d.key, newValue); }}
          />
        );
      })}
    </div>
  );
}
